import React, { useEffect, useState } from "react";
import {
  getPendingApprovalAging,
  getPendingPoAging,
  getApprovalTimeliness,
  getPoTimeliness,
  getInvoiceTimeliness,
  getCostCountAnalytics,
  getInvalidCreditNoteOverview,
  getRejectionDashboard,
  getCxTimelinessOverview,
} from "../../../services/metricsApi";
import { formatCompact, formatCount } from "../StatTile";
import WarningBanner from "../WarningBanner";
import "../Analytics.css";

// One card per backend dashboard (Gap #9 in the metrics reference: there's no
// single "overview" endpoint, so this fires all 8 in parallel). Every card
// jumps to its full section further down the single-scroll page.
const CARDS = [
  {
    key: "pending_approval_aging",
    title: "Pending Approval Aging",
    sectionId: "pending-approval",
    fetcher: getPendingApprovalAging,
    metrics: (d) => [
      { label: "Pending Count", value: formatCompact(d.total_count) },
      { label: "Pending Amount", value: formatCompact(d.total_amount, { currency: true }) },
    ],
  },
  {
    key: "pending_po_aging",
    title: "Pending PO Aging",
    sectionId: "pending-po",
    fetcher: getPendingPoAging,
    metrics: (d) => [
      { label: "Pending Count", value: formatCompact(d.total_count) },
      { label: "Pending Amount", value: formatCompact(d.total_amount, { currency: true }) },
    ],
  },
  {
    key: "approval_timeliness",
    title: "Approval Timeliness",
    sectionId: "approval-timeliness",
    fetcher: getApprovalTimeliness,
    metrics: (d) => [
      { label: "Within SLA", value: `${(d.kpi_cards?.within_sla_pct ?? 0).toFixed(1)}%` },
      { label: "Avg Days", value: (d.kpi_cards?.avg_approval ?? 0).toFixed(1) },
    ],
  },
  {
    key: "po_timeliness",
    title: "PO Timeliness",
    sectionId: "po-timeliness",
    fetcher: getPoTimeliness,
    metrics: (d) => [
      { label: "Within SLA", value: `${(d.kpi_cards?.within_sla_pct ?? 0).toFixed(1)}%` },
      { label: "Avg Days", value: (d.kpi_cards?.avg_po_creation ?? 0).toFixed(1) },
    ],
  },
  {
    key: "invoice_timeliness",
    title: "Invoice Timeliness",
    sectionId: "invoice-timeliness",
    fetcher: getInvoiceTimeliness,
    metrics: (d) => [
      { label: "Within SLA", value: `${(d.kpi_cards?.within_sla_pct ?? 0).toFixed(1)}%` },
      { label: "Avg Days", value: (d.kpi_cards?.avg_invoice_creation ?? 0).toFixed(1) },
    ],
  },
  {
    key: "cost_count",
    title: "Cost & Count Analytics",
    sectionId: "cost-count",
    fetcher: getCostCountAnalytics,
    metrics: (d) => [
      { label: "Total Cost (USD)", value: formatCompact(d.kpi_cards?.total_cost?.value, { currency: true }) },
      { label: "Total Count", value: formatCompact(d.kpi_cards?.total_count?.count) },
    ],
  },
  {
    key: "invalid_credit_note",
    title: "Invalid & Credit Notes",
    sectionId: "invalid-credit-note",
    fetcher: getInvalidCreditNoteOverview,
    metrics: (d) => [
      { label: "Invalid Requests", value: formatCount(d.kpi_cards?.total_invalid_requests?.count) },
      { label: "Credit Note Applicable", value: formatCount(d.kpi_cards?.total_credit_note_applicable?.count) },
      { label: "Credit Note Available", value: formatCount(d.kpi_cards?.credit_note_available?.count) },
      { label: "Credit Note Pending", value: formatCount(d.kpi_cards?.credit_note_pending?.count) },
    ],
  },
  {
    key: "rejection",
    title: "Rejection Overview",
    sectionId: "rejection",
    fetcher: getRejectionDashboard,
    metrics: (d) => [
      { label: "Total Rejected", value: formatCount(d.kpi_cards?.total_rejected_requests?.count) },
      {
        label: "Event / Cost",
        value: `${formatCount(d.kpi_cards?.event_rejected?.count)} / ${formatCount(d.kpi_cards?.cost_rejected?.count)}`,
      },
    ],
  },
  {
    key: "cx_timeliness",
    title: "CX Timeliness",
    sectionId: "cx-timeliness",
    fetcher: getCxTimelinessOverview,
    metrics: (d) => [
      { label: "Overall Unplanned Charges", value: `${d.overall.within_sla_pct.toFixed(1)}%` },
      { label: "Detention & Demurrage", value: `${d.dnd.within_sla_pct.toFixed(1)}%` },
    ],
  },
];

const AtAGlanceSection = ({ id, filters, onJumpTo }) => {
  const [results, setResults] = useState({});

  useEffect(() => {
    let cancelled = false;
    setResults((prev) =>
      CARDS.reduce((acc, c) => ({ ...acc, [c.key]: { ...prev[c.key], loading: true } }), {})
    );

    CARDS.forEach((card) => {
      card
        .fetcher(filters)
        .then((data) => {
          if (cancelled) return;
          setResults((prev) => ({ ...prev, [card.key]: { loading: false, data } }));
        })
        .catch((err) => {
          if (cancelled) return;
          setResults((prev) => ({ ...prev, [card.key]: { loading: false, error: err.message } }));
        });
    });

    return () => {
      cancelled = true;
    };
  }, [filters]);

  return (
    <section id={id} className="analytics-section">
      <div className="analytics-section-header">
        <h2 className="analytics-section-heading">At a Glance</h2>
        <span className="analytics-section-caption">All 9 dashboards — click a card to jump to it</span>
      </div>

      <div className="overview-card-grid">
        {CARDS.map((card) => {
          const result = results[card.key] || {};
          return (
            <div
              key={card.key}
              className={`overview-card overview-card-clickable${card.key === "cx_timeliness" ? " overview-card-cx-timeliness" : ""}`}
              role="button"
              tabIndex={0}
              onClick={() => onJumpTo(card.sectionId)}
              onKeyDown={(e) => {
                if (e.key === "Enter") onJumpTo(card.sectionId);
              }}
            >
              <h4 className="overview-card-title">{card.title}</h4>

              {result.loading && <span className="overview-card-loading">Loading…</span>}
              {result.error && <span className="overview-card-error">Failed to load</span>}

              {result.data && (
                <>
                  <WarningBanner warning={result.data.warning} />
                  <div className="overview-card-metrics">
                    {card.metrics(result.data).slice(0, 2).map((m) => (
                      <div key={m.label}>
                        <span className="overview-card-metric-label">{m.label}</span>
                        <span className="overview-card-metric-value">{m.value}</span>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
};

export default AtAGlanceSection;
